import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import PauseModal from "../../components/PauseModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const reducer = (state, action) => {
  if (action.type === "LOAD_PAUSES") return [...action.payload];
  if (action.type === "UPDATE_PAUSES") {
    const pause = action.payload;
    const pauseIndex = state.findIndex((s) => s.id === pause.id);
    if (pauseIndex !== -1) {
      state[pauseIndex] = pause;
      return [...state];
    }
    return [pause, ...state];
  }
  if (action.type === "DELETE_PAUSE") {
    return state.filter((p) => p.id !== action.payload);
  }
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    backgroundColor: theme.palette.background.default,
    border: "none",
    ...theme.scrollbarStyles,
  },

  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
  },
  addButton: {
    background: theme.palette.background.paper,
    color: "#6517ab",
    borderRadius: "12px",
    padding: "10px 24px",
    fontWeight: "700",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.type === 'light' ? "#f0f0f0" : "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    },
  },

  tableRow: {
    transition: "all 0.2s",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.palette.type === 'light' ? "rgba(101, 23, 171, 0.05)" : "rgba(255, 255, 255, 0.05)",
      transform: "scale(1.002)",
    },
  },

  tableHeaderCustom: {
    backgroundColor: theme.palette.background.paper,
    "& th": {
      fontWeight: 700,
      color: "#6517ab",
      fontSize: "0.95rem",
      borderBottom: `2px solid ${theme.palette.divider}`,
    },
  },

  actionButton: {
    borderRadius: "10px",
    padding: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
}));

const Pauses = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [pauses, dispatch] = useReducer(reducer, []);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [selectedPause, setSelectedPause] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingPause, setDeletingPause] = useState(null);

  useEffect(() => {
    const fetchPauses = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/pauses");
        dispatch({ type: "LOAD_PAUSES", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPauses();
  }, []);

  const handleOpenPauseModal = () => {
    setSelectedPause(null);
    setPauseModalOpen(true);
  };

  const handleClosePauseModal = () => {
    setSelectedPause(null);
    setPauseModalOpen(false);
  };

  const handleEditPause = (pause) => {
    setSelectedPause(pause);
    setPauseModalOpen(true);
  };

  const handleDeletePause = async (pauseId) => {
    try {
      await api.delete(`/pauses/${pauseId}`);
      toast.success("Pausa excluída!");
      dispatch({ type: "DELETE_PAUSE", payload: pauseId });
    } catch (err) {
      toastError(err);
    }
    setDeletingPause(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingPause && "Excluir Pausa"}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeletePause(deletingPause.id)}
      >
        Você tem certeza que deseja excluir esta pausa?
      </ConfirmationModal>
      <PauseModal
        open={pauseModalOpen}
        onClose={handleClosePauseModal}
        pauseId={selectedPause?.id}
        onSave={(pause) => dispatch({ type: "UPDATE_PAUSES", payload: pause })}
      />

      <Box className={classes.headerContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" className={classes.headerTitle}>Pausas</Typography>
            <Typography className={classes.headerSubtitle}>Configure os intervalos e pausas de sua equipe</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent={isMobile ? "center" : "flex-end"}>
              <Button
                variant="contained"
                className={classes.addButton}
                startIcon={<AddIcon />}
                onClick={handleOpenPauseModal}
              >
                Adicionar Pausa
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Paper className={classes.mainPaper} elevation={0}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead className={classes.tableHeaderCustom}>
            <TableRow>
              <TableCell>Nome da Pausa</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={2} />
            ) : (
              pauses.map((pause) => (
                <TableRow key={pause.id} className={classes.tableRow}>
                  <TableCell>
                    <Typography variant="body1" style={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      {pause.name}
                    </Typography>

                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditPause(pause)}
                        style={{ 
                          backgroundColor: theme.palette.type === 'dark' ? "rgba(25, 118, 210, 0.1)" : "#e3f2fd", 
                          color: "#1976d2" 
                        }}
                        className={classes.actionButton}
                        title="Editar"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingPause(pause);
                          setConfirmModalOpen(true);
                        }}
                        style={{ 
                          backgroundColor: theme.palette.type === 'dark' ? "rgba(211, 47, 47, 0.1)" : "#ffebee", 
                          color: "#d32f2f" 
                        }}
                        className={classes.actionButton}
                        title="Excluir"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};
export default Pauses;
